import { useState, useEffect, useCallback, Fragment } from "react";
import * as drawing from "@progress/kendo-drawing";
import {
  Chart,
  ChartSeries,
  ChartSeriesItem,
  ChartTooltip,
  ChartXAxis,
  ChartXAxisItem,
  ChartYAxis,
  ChartYAxisItem,
} from "@progress/kendo-react-charts";
import { RadioGroup } from "@progress/kendo-react-inputs";
import { parseDate } from "@progress/kendo-intl";
import { Error } from "@progress/kendo-react-labels";
import { Card, CardBody, CardSubtitle } from "@progress/kendo-react-layout";
import contractsApi from "../../api/contracts";
import { useApi } from "../../hooks/useApi";
import DateUtility from "../../utilities/dateUtilities";
import MathUtility from "../../utilities/mathUtilities";
import { LoadingIndicator } from "../LoadingIndicator";
import { volatilityColors } from "./palette";

interface IVolatilityChartProps {
  commodity: any | null;
  refresh?: number;
}

interface IVolatilityItem {
  contract: string;
  moneyness: number;
  strike: number;
  volatility: number;
}

const ColorRow = ({ color }: any) => (
  <div style={{ display: "flex", width: "100%" }}>
    {volatilityColors.map((vColor, index) => (
      <div
        key={index}
        style={{
          backgroundColor: color ?? vColor,
          flex: 1,
          aspectRatio: "1",
        }}
        title={color}
      />
    ))}
  </div>
);

const LegendLine = ({ caption, color }: any) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
    <div style={{ flexBasis: "25%", font: "small-caption" }}>{caption}</div>
    <div style={{ flexBasis: "60%" }}>
      <ColorRow color={color} />
    </div>
  </div>
);

export const VolatilityChart = ({ commodity, refresh }: IVolatilityChartProps) => {
  const getVolatilityDataApi = useApi(contractsApi.getVolatilityData);

  const yValueTypes: any = [
    { label: "Moneyness", field: "moneyness", value: 0 },
    { label: "Strike", field: "strike", value: 1 },
  ];

  const [commodityId, setCommodityId] = useState(0);
  const [chartData, setChartData] = useState<IVolatilityItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [yIndex, setYIndex] = useState(0);
  const [yLabels, setYLabels] = useState<number[]>([]);
  const [atm, setAtm] = useState<any[]>([]);
  const atmColor = "#000";
  const smallFont = "small-caption";

  useEffect(() => {
    setCommodityId(commodity?.id ?? 0);
  }, [commodity]);

  useEffect(() => {
    getData(commodityId);
  }, [commodityId, refresh]);

  useEffect(() => {
    setIsLoading(getVolatilityDataApi.loading);
  }, [getVolatilityDataApi.loading]);

  useEffect(() => {
    if (!getVolatilityDataApi.data?.data) return;

    const fields = yValueTypes.map((t: any) => t.field);

    // Create chart data
    const data = getVolatilityDataApi.data.data.map((item: IVolatilityItem) => {
      const filtered = Object.fromEntries(
        fields.map((key: string) => {
          const val = item[key as keyof IVolatilityItem];
          const rounded = typeof val === "number" ? MathUtility.round(val, 2) : val;
          return [key, rounded];
        }),
      );

      return {
        x: new Date(parseDate(item.contract)),
        volatility: item.volatility,
        ...filtered,
      };
    });

    // Create ATM data
    setAtm(
      data
        .filter((d: any) => d.moneyness === 1)
        .map((item: any) => ({
          x: item.x,
          y: yValueTypes.map((t: any) => item[t.field]),
        })),
    );

    setChartData(data.sort((a, b) => a.moneyness - b.moneyness));
  }, [getVolatilityDataApi.data]);

  useEffect(() => {
    const field = yValueTypes[yIndex].field;
    const yValues = getUniqueValues(chartData, field);
    const targetYValues = selectValuesFromArray(yValues, 20);
    setYLabels(targetYValues);
  }, [yIndex, chartData]);

  function selectValuesFromArray<T>(array: T[], n: number): T[] {
    const result: T[] = [];
    const len = array.length;

    if (n <= 0) return result;
    if (n >= len) return [...array];

    // Include first
    result.push(array[0]);

    for (let i = 1; i < n - 1; i++) {
      const index = Math.round((i * (len - 1)) / (n - 1));
      result.push(array[index]);
    }

    // Include last
    result.push(array[len - 1]);

    return result;
  }

  const getUniqueValues = (data: any[], field: string): number[] => {
    const values = data.map((d) => d[field]);
    return Array.from(new Set(values)).sort((a, b) => a - b);
  };

  const getData = useCallback(async (commodityId: number) => {
    if (commodityId > 0) {
      getVolatilityDataApi.request(commodityId);
    }
  }, []);

  const renderTooltip = ({ point }: any) => {
    const { value } = point;

    return (
      <Fragment>
        <div>{DateUtility.formatDateMY(value.x)}</div>
        <div>
          {yValueTypes[yIndex].label}: {value.y}
        </div>
        <div>Volatility: {MathUtility.round(value.value, 4)}</div>
      </Fragment>
    );
  };

  const renderColor = (e: any) => {
    const { value } = e;
    if (atm.find((d) => d.x === value.x && d.y[yIndex] === value.y)) return atmColor;

    const clamped = Math.max(e.min, Math.min(e.max, value.value));
    const normalized = (clamped - e.min) / (e.max - e.min);
    const index = Math.floor(normalized * (volatilityColors.length - 1));
    return volatilityColors[index];
  };

  return (
    <>
      <LoadingIndicator loading={isLoading} />

      {commodity && !isLoading && (
        <Card>
          <CardBody>
            <CardSubtitle>
              <div
                style={{
                  marginRight: "1rem",
                  display: "flex",
                  alignItems: "baseline",
                  justifyContent: "space-between",
                }}
              >
                <div>Volatility</div>
                {chartData.length > 0 && (
                  <div>
                    <RadioGroup
                      data={yValueTypes}
                      value={yIndex}
                      layout="horizontal"
                      onChange={(e) => setYIndex(e.value)}
                    />
                  </div>
                )}
              </div>
            </CardSubtitle>

            <Error>{getVolatilityDataApi.error}</Error>

            {chartData.length > 0 ? (
              <>
                <Chart>
                  <ChartSeries>
                    <ChartSeriesItem
                      type="heatmap"
                      data={chartData}
                      xField="x"
                      yField={yValueTypes[yIndex].field}
                      field="volatility"
                      color={renderColor}
                      labels={{
                        visible: false,
                      }}
                    ></ChartSeriesItem>
                  </ChartSeries>
                  <ChartXAxis>
                    <ChartXAxisItem
                      labels={{ rotation: "auto", format: "MMM yyyy", font: smallFont }}
                    />
                  </ChartXAxis>
                  <ChartYAxis>
                    <ChartYAxisItem
                      labels={{
                        visual: (e) => {
                          const { value, rect, options } = e;

                          if (yLabels.includes(value)) {
                            const text = new drawing.Text(
                              value.toString(),
                              [rect.origin.x, rect.origin.y],
                              {
                                font: smallFont,
                                fill: {
                                  color: options.color,
                                },
                              },
                            );
                            return text;
                          }

                          return null; // Hide other labels
                        },
                      }}
                      title={{ text: yValueTypes[yIndex].label }}
                    />
                  </ChartYAxis>
                  <ChartTooltip render={renderTooltip} />
                </Chart>

                <div style={{ margin: "1rem" }}>
                  <LegendLine caption="Low to High Volatility" />
                  <LegendLine caption="At The Money" color={atmColor} />
                </div>
              </>
            ) : (
              <div>Volatility data is unavailable for this commodity</div>
            )}
          </CardBody>
        </Card>
      )}
    </>
  );
};
